import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import VoiceInputButton from '@/components/VoiceInputButton';
import { ArrowLeft, ArrowRight, Lock, Sparkles, Heart, Info } from 'lucide-react';
import { toast } from 'sonner';
import { ProgressBar } from '@/components/lesson/ProgressBar';
import { buildSteps, buildLegacySteps } from '@/components/lesson/buildSteps';
import {
  StepWelcome, StepWhy, StepSection, StepTherapist, StepEvidence,
  StepScenario, StepActivity, StepChallenge, StepComplete,
} from '@/components/lesson/Steps';

// Lessons that unlock an inline assessment (rendered above the persistent Next button)
const LESSON_ASSESSMENTS = {
  rel_attachment: {
    title: 'Take the Attachment Styles Assessment',
    subtitle: '16 evidence-informed questions, ~4 minutes. Educational, not diagnostic. See your dominant style + AI-powered, culturally responsive guidance.',
    to: '/app/attachment',
    testid: 'lesson-attachment-cta',
  },
};

export default function LessonDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completionsCount, setCompletionsCount] = useState(0);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [reflection, setReflection] = useState('');

  useEffect(() => {
    http.get(`/learn/lessons/${id}`).then(r => setLesson(r.data)).catch(() => nav('/app/learn'));
    http.get('/learn/completions').then(r => { setCompleted(r.data.some(c => c.lesson_id === id)); setCompletionsCount(r.data.length); });
  }, [id, nav]);

  useEffect(() => {
    if (!lesson) return;
    try {
      const raw = localStorage.getItem(`bp_lesson_${id}`);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.step === 'number') setStep(p.step);
        if (typeof p.reflection === 'string') setReflection(p.reflection);
      }
    } catch { /* ignore */ }
  }, [lesson, id]);

  const isRich = lesson?.schema_version === 2;
  const steps = useMemo(() => {
    if (!lesson) return [];
    return isRich ? buildSteps(lesson) : buildLegacySteps(lesson);
  }, [lesson, isRich]);

  useEffect(() => {
    if (!lesson) return;
    localStorage.setItem(`bp_lesson_${id}`, JSON.stringify({ step, reflection }));
    // Report partial progress to backend so "Continue where you left off" & filters work
    const total = steps.length || 1;
    http.post(`/learn/lessons/${id}/progress`, { step, total }).catch(()=>{});
  }, [step, reflection, lesson, id, steps.length]);

  const goNext = () => {
    if (step < steps.length - 1) {
      setDirection(1);
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const goBack = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      nav(-1);
    }
  };
  const complete = async () => {
    if (!completed) {
      try {
        await http.post(`/learn/lessons/${id}/complete`);
        setCompleted(true);
        toast.success('Lesson marked complete');
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't save your progress. Please try again.");
      }
    }
  };
  const onCoach = async () => { await complete(); nav('/app/coach'); };
  const onFinish = async () => { await complete(); localStorage.removeItem(`bp_lesson_${id}`); nav('/app/learn'); };

  if (!lesson) return <div className="p-6 text-navy/60" data-testid="lesson-loading">Loading…</div>;

  const FREE_TRIAL_LESSONS = 7;
  const trialLocked = !!user?.trial?.locked;
  // Trial members: premium lessons are locked as usual, and free lessons are
  // capped — after FREE_TRIAL_LESSONS completed, remaining lessons prompt upgrade.
  const capReached = trialLocked && !completed && completionsCount >= FREE_TRIAL_LESSONS;
  const locked = (lesson.premium && !user?.is_premium) || capReached;
  if (locked) {
    return (
      <div className="p-6 space-y-6" data-testid="lesson-detail">
        <button onClick={() => nav(-1)} data-testid="lesson-back" className="flex items-center gap-2 text-sm text-navy/60"><ArrowLeft size={14}/>Back</button>
        <div className="bg-white rounded-3xl p-8 card-soft text-center">
          <Lock className="mx-auto text-gold mb-4" size={32} strokeWidth={1.5}/>
          <h2 className="font-heading text-2xl mb-2">{capReached ? 'You’ve used your free trial lessons' : 'Included with Brotherhood+'}</h2>
          <p className="text-navy/60">{capReached
            ? `Your trial includes ${FREE_TRIAL_LESSONS} free lessons. Upgrade to Brotherhood+ for the full learning library.`
            : 'This lesson is part of Brotherhood University.'}</p>
          <button onClick={() => nav('/subscription/checkout')} data-testid="lesson-upgrade-btn"
            className="mt-5 bg-navy text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-navy-soft transition-smooth">
            Upgrade to full access
          </button>
        </div>
      </div>
    );
  }

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const nextLabel = isLast ? null : (step === 0 ? 'Begin lesson' : 'Next');
  const assessment = LESSON_ASSESSMENTS[id];

  const renderStep = () => {
    if (!current) return null;
    if (current.type === 'welcome') return <StepWelcome lesson={lesson}/>;
    if (current.type === 'why') return <StepWhy lesson={lesson}/>;
    if (current.type === 'section') return <StepSection section={lesson.sections[current.index]}/>;
    if (current.type === 'therapist') return <StepTherapist lesson={lesson}/>;
    if (current.type === 'evidence') return <StepEvidence ev={lesson.evidence_spotlight}/>;
    if (current.type === 'scenario') return <StepScenario sc={lesson.scenario}/>;
    if (current.type === 'activity') return <StepActivity activity={lesson.activity} reflection={reflection} setReflection={setReflection}/>;
    if (current.type === 'challenge') return <StepChallenge ap={lesson.action_plan}/>;
    if (current.type === 'complete') return <StepComplete lesson={lesson} onCoach={onCoach} onFinish={onFinish}/>;
    if (current.type === 'legacy_welcome') return (
      <div className="text-center pt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-3">{lesson.duration}</p>
        <h1 className="font-heading text-4xl leading-tight text-navy">{lesson.title}</h1>
        <p className="text-navy/70 mt-4 text-lg leading-relaxed max-w-md mx-auto">{lesson.summary}</p>
      </div>
    );
    if (current.type === 'legacy_body') return (
      <div className="pt-4">
        <p className="text-[10px] uppercase tracking-widest text-navy/50 font-semibold mb-3 flex items-center gap-2"><Sparkles size={12}/>Core Learning</p>
        <div className="bg-white rounded-3xl p-7 card-soft whitespace-pre-wrap text-navy/85 leading-[1.8] text-[16px]" data-testid="lesson-body">
          {lesson.body}
        </div>
      </div>
    );
    if (current.type === 'legacy_reflection') return (
      <div className="pt-4">
        <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-3">Reflection</p>
        <h2 className="font-heading text-2xl sm:text-3xl text-navy mb-6">{lesson.reflection}</h2>
        <div className="relative">
          <textarea
            rows={7}
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            data-testid="reflection-textarea"
            placeholder="Write your thoughts here — private and autosaved."
            className="w-full bg-white border border-navy/10 rounded-2xl p-5 pr-14 focus:outline-none focus:ring-2 focus:ring-gold text-navy leading-relaxed text-[16px]"/>
          <div className="absolute right-3 bottom-3">
            <VoiceInputButton compact testid="lesson-reflection-voice-btn"
              onTranscript={(chunk) => setReflection(prev => (prev + ' ' + chunk).trim())}/>
          </div>
        </div>
      </div>
    );
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col" data-testid="lesson-detail">
      <div className="px-6">
        <ProgressBar current={step} total={steps.length}/>
      </div>

      <div className="px-6 flex-1 max-w-2xl lg:max-w-3xl w-full mx-auto pb-32 lg:pb-24">
        <button onClick={goBack} data-testid="lesson-back" className="flex items-center gap-2 text-sm text-navy/60 mb-6 mt-2 hover:text-navy transition-smooth">
          <ArrowLeft size={14}/>{step === 0 ? 'Back' : 'Previous step'}
        </button>

        {lesson.category === 'financial' && (
          <div data-testid="lesson-financial-disclaimer" className="mb-5 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/[0.06] px-4 py-3">
            <Info size={16} className="text-gold shrink-0 mt-0.5"/>
            <p className="text-xs text-navy/70 leading-relaxed">
              <span className="font-semibold text-navy">Educational only — not financial advice.</span> Brotherhood+ is not a financial advisor and this lesson is for general education. For decisions about your money, debt, taxes, or investments, consult a licensed financial professional.
            </p>
          </div>
        )}

        {(lesson.category === 'physical' || lesson.category === 'sexual_health') && (
          <div data-testid="lesson-medical-disclaimer" className="mb-5 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/[0.06] px-4 py-3">
            <Info size={16} className="text-gold shrink-0 mt-0.5"/>
            <p className="text-xs text-navy/70 leading-relaxed">
              <span className="font-semibold text-navy">Educational only — not medical advice.</span> Brotherhood+ is not a healthcare provider and this lesson is for general education. It is not a diagnosis, treatment, or a substitute for professional medical care. For anything related to your health — including blood pressure, medications, or symptoms — talk to a licensed doctor or clinician.
            </p>
          </div>
        )}

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: direction * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -40 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`step-${current?.type}`}>
            {renderStep()}
          </motion.div>
        </AnimatePresence>

        {/* Inline assessment CTA — appears on the completion step of lessons that have a paired assessment */}
        {assessment && isLast && (
          <div className="mt-6 bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid={`${assessment.testid}-block`}>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-2xl bg-beige flex items-center justify-center shrink-0">
                <Heart size={18} className="text-gold"/>
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Deepen the work</p>
                <p className="font-heading text-lg lg:text-xl text-navy mt-1 leading-snug">{assessment.title}</p>
                <p className="text-sm text-navy/60 mt-1 leading-relaxed">{assessment.subtitle}</p>
                <Link to={assessment.to} data-testid={assessment.testid}
                  className="inline-flex items-center gap-2 mt-4 bg-navy text-white rounded-full px-5 py-3 text-sm font-medium hover:bg-navy-soft transition-smooth">
                  Take the assessment <ArrowRight size={14}/>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {!isLast && (
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 bg-[#F9F8F6]/95 backdrop-blur border-t border-navy/5 px-6 py-4 lg:py-5">
          <div className="max-w-2xl lg:max-w-3xl mx-auto">
            <button
              onClick={goNext}
              data-testid="lesson-next"
              className="w-full bg-navy text-white rounded-full py-4 lg:py-5 font-medium hover:bg-navy-soft transition-smooth flex items-center justify-center gap-2 text-base">
              {nextLabel} <ArrowRight size={18}/>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
